// frontend/src/components/homepage/HeroCarouselLive.js
//
// THE PHONE HERO WHEEL (owner round 5, 2026-09-06).
//
// On a wide screen the hero shows ONE lead card that grows out of an ignited
// node in the mesh, counts its score, holds and files itself away
// (useHeroCycle.js). That figure does not translate to a phone: the flight is a
// full-screen transform competing with the page scroll, there is no empty page
// beside the stage for a node to ignite in, and a single card that swaps itself
// out every six seconds gives a visitor no way to look at the one they just saw.
//
// So under the hero's own 900px breakpoint the same deck becomes a WHEEL the
// visitor owns: the current card face-on at the centre, its two neighbours
// swung back and away on a cylinder, the next two barely there behind them, and
// the rest parked out of sight - turned with the finger and turning itself
// every 5s until it is touched.
//
// WHY THIS IS NOT THE SCROLL-SNAP STRIP IT REPLACED (round 4 -> 5). The strip
// was a real scroller: eight cards in a row, `scroll-snap-type: x mandatory`,
// the browser's own momentum. Cheap and native - and it had ONE end. After the
// eighth card it had to rewind, and a rewind is the visitor watching seven cards
// fly past backwards. The owner's words: "the carousel must be infinite without
// the user noticing when the loop ends - the first card comes from the same
// direction as the others." A scroller cannot do that without cloning the deck
// and teleporting `scrollLeft` mid-gesture. A MODULAR INDEX can, for free:
//
//   * The truth is one integer, `current`, in 0..N-1. There is no scroll
//     position, no clone and no seam.
//   * Every slide's place is decided by its SHORTEST modular distance from that
//     index - `wrapDelta` - so at current=7 the first card is already sitting at
//     +1, one slot away on the incoming side, exactly where card 3 sits when
//     current=2. The wrap is not a special case in this file; it is what the
//     arithmetic does.
//   * Only |d| <= 2 is drawn. The other N-5 sit at the far anchor, transparent
//     and out of the accessibility tree, so a jump between two far slots (the
//     one thing the wrap does move a long way) is a jump between two invisible
//     positions.
//
// THE WHEEL, AND WHY THE TRANSFORM IS IN THAT ORDER. Each slot is
// `rotateY() translateX() translateZ() scale()`, and the rotation comes FIRST on
// purpose: the translate that follows runs along the card's own, already-turned
// x axis, so a neighbour is carried sideways AND into the screen by one
// operation. That is a cylinder rather than a fanned-out stack of flat cards -
// the "wheel of fortune, not a straight line" the round asked for. The centre
// card is `rotateY(0) translateZ(0) scale(1)`: untouched by the 3D, pixel for
// pixel the card it would be with no wheel around it.
//
// THE FINGER. `touch-action: pan-y` gives the horizontal axis to this component
// and leaves the vertical one to the page, so a visitor scrolling past the hero
// never fights the wheel. A drag writes the SAME interpolated layout the turn
// animates through, at a fractional index, so the wheel under the finger is the
// wheel mid-turn - not a second, simpler behaviour that hands over at the end.
// Past one slot it rubber-bands: the deck is infinite but a gesture is not, and
// a flick that spun four cards would undo the point of the wheel.
//
// ONE WRITER PER SLIDE. React renders the slides with NO inline style at all and
// never sets one; `applyLayout` owns `transform`, `opacity`, `visibility`,
// `zIndex`, `transition`, `tabIndex`, `aria-hidden` and `inert` on all N, and it
// is called from a layout effect (before paint) and from the drag's frame.
// Splitting those between JSX and imperative writes is how a drag ends up
// fighting a re-render it did not cause.
//
// `will-change` is the ONE presentational property this function does NOT
// write, and that is measured, not stylistic: it lives statically on every
// slide in homepage.css. Toggling it per turn - promising the compositor the
// five slots on the wheel and taking the promise back from the rest - creates
// and destroys layers on every single turn, and cost 43ms of main-thread work
// per frame against 21ms for the static form (390px, DPR 2, 4x CPU, measured
// 2026-09-06). The three parked slides are `visibility: hidden`, so the
// standing hint buys them no texture anyway.
//
// MOTION OFF: no timer is armed at all, every card is rendered at its finished
// state on the first paint, and every layout write is instant - `applyLayout`
// is handed `animate: false`, so a turn is a cut. THE WHEEL STILL TURNS by
// finger and by arrow key: a visitor who asked us to stop animating did not ask
// to lose seven of the eight cards.
//
// NO DOTS (round 5b, owner). The wheel carries its own position indicator - two
// cards are always half-visible beside the current one - so a dot row under it
// was a second, redundant one. What the dots WERE carrying is the keyboard and
// screen-reader path, and that moved onto the current slide itself: it is the
// wheel's single tab stop, it announces "3 of 8", and the arrow keys turn from
// it. A carousel that only a finger can turn is content half our visitors
// cannot reach, so that half of the dots' job could not simply be dropped.
//
// PRERENDER: this component is not mounted. HeroLive gates it on `hydrated`, so
// the snapshot (and the hydrating client's first pass) carries the desktop
// single-card figure at every width, and the phone swaps to the wheel one
// render later, before paint. See motion/useHydrated.js.
//
// SCORE WRITING follows HeroLive's rule exactly: the count hands a value per
// frame for 1.2s and routing that through setState would re-render every slide
// ~70 times per card. Each card is RENDERED once with its initial score (the
// finished value when motion is off, 0 when it is going to count) and the count
// writes the two nodes that show it - the figure and the bar - through refs.
// React never renders a different `score`, so it never touches those nodes.

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Box } from "@mui/material";
import LeadCardLive from "./LeadCardLive";
import { countUp, createTicker, SCORE_COUNT_MS } from "./useHeroCycle";
import rafThrottle from "../../motion/rafThrottle";

/**
 * How long a card holds before the wheel turns itself. 7s, not the strip's 5:
 * a card here is a paragraph of Hebrew to read, and the turn that follows now
 * takes 1.2s of it (owner round 5b, "slow the card replacement down").
 */
export const AUTO_ADVANCE_MS = 7000;
/**
 * How long the auto-advance stays out of the way after the visitor touches the
 * wheel. Long enough to read the card they chose; the timer re-arms from the
 * moment the finger LIFTS, not from the moment it landed.
 */
export const MANUAL_PAUSE_MS = 10000;
/**
 * The turn. 1.2s on the page's own ease - the owner's round-5 note was "much
 * more pleasant and elegant, less sharp and quick", and 5b slowed it again from
 * 900ms. This is the same curve every other reveal on the page uses, run slow
 * enough to read as a wheel rotating rather than a card being swapped.
 */
export const WHEEL_TURN_MS = 1200;
export const WHEEL_EASE = "cubic-bezier(.22,1,.36,1)";

/**
 * THE WHEEL'S FIVE ANCHORS, indexed by |slot distance| 0..3.
 *
 * Rows 0-2 are the owner's literals (centre; `rotateY(38deg) translateX(72%)
 * translateZ(-90px) scale(.86)` at opacity .55; opacity .18 / scale .74 at two
 * slots out). Row 3 is where a card is parked once it is off the wheel -
 * invisible, so its exact values are only ever seen as the far end of an
 * interpolation.
 *
 * A drag sits BETWEEN two rows, so every value is interpolated linearly and the
 * anchors are hit exactly at rest. That is what makes the finger and the turn
 * the same motion.
 */
const ANCHORS = {
  rotate: [0, 38, 52, 58],
  translate: [0, 72, 128, 168], // percent of the card's own width
  depth: [0, -90, -190, -240], // px
  scale: [1, 0.86, 0.74, 0.68],
  opacity: [1, 0.55, 0.18, 0],
};
/** Slots past this are parked: not drawn, and out of the accessibility tree. */
const RENDERED_SPAN = 2;
/**
 * How far a neighbour's CENTRE actually travels, as a fraction of the card's
 * width: `translateX(72%)` along an axis already turned 38deg contributes
 * 0.72 * cos(38deg) on screen. The finger is divided by this, so the card under
 * it moves with it 1:1 rather than at some invented sensitivity.
 */
const SLOT_TRAVEL = 0.72 * Math.cos((38 * Math.PI) / 180); // 0.5674

/** Past this the gesture is a flick and takes one card regardless of distance. */
const FLICK_PX_PER_MS = 0.4;
/** How far a finger must commit before the wheel decides the gesture is its own. */
const GESTURE_SLOP = 6;

/**
 * The a11y strings.
 *
 * NEW WORDING (approved with the strip, 2026-09-05): the carousel's own label
 * and the per-slide position label. Nothing here is visible copy - the wheel
 * shows the same card text the desktop hero does.
 *
 * The dot row that carried a third string was REMOVED in round 5b at the
 * owner's request, so `slide` is now the ONLY thing that tells a
 * screen-reader visitor where in the deck they are. It is on the focusable
 * current slide, which is also what the arrow keys act on.
 */
const CAROUSEL_COPY = {
  he: {
    label: "הזדמנויות לדוגמה",
    slide: (index, total) => `${index} מתוך ${total}`,
  },
  en: {
    label: "Sample opportunities",
    slide: (index, total) => `${index} of ${total}`,
  },
};

// A STATIC sx OBJECT, hoisted. LeadCardLive is memoised and an inline
// `sx={{...}}` is a fresh object on every render - a changed prop, so the memo
// would never hit and all eight cards would re-render together.
//
// `height: 100%` is what makes the wheel's height stable: every slide is in the
// SAME grid cell, so the row is the tallest card's height and each card
// stretches to it. No measurement, no `document.fonts.ready` pass, no layout
// shift under the finger - and no transform in this file changes layout, so the
// stage cannot shift while it turns either.
const slideCardSx = { width: "100%", maxWidth: "none", height: "100%" };

const clamp = (value, low, high) => (value < low ? low : value > high ? high : value);
const wrapIndex = (raw, total) => ((raw % total) + total) % total;

/**
 * The shortest signed distance from `pos` to slide `index`, in slots.
 *
 * THIS IS THE WHOLE INFINITE-LOOP TRICK. With eight cards and the wheel on card
 * 7, card 0 is not "seven cards away and behind"; it is +1 - one slot away on
 * the incoming side - so it arrives from the same direction, at the same speed,
 * as every other card. `pos` is fractional while a finger is down.
 */
export function wrapDelta(index, pos, total) {
  const half = total / 2;
  let d = (index - pos) % total;
  if (d > half) d -= total;
  if (d <= -half) d += total;
  return d;
}

/** Linear interpolation across the anchor table at a fractional distance. */
const at = (table, distance) => {
  const a = clamp(distance, 0, table.length - 1);
  const low = Math.floor(a);
  const high = Math.min(low + 1, table.length - 1);
  return table[low] + (table[high] - table[low]) * (a - low);
};

/**
 * Beyond one slot the wheel resists: the deck is infinite but a single gesture
 * is not, and the release always lands one card away at most. 22% of the extra
 * travel, itself capped, so a hard fling stops at a visibly "held" 1.18 slots
 * instead of spinning.
 */
const rubberBand = (slots) => {
  const magnitude = Math.abs(slots);
  if (magnitude <= 1) return slots;
  return Math.sign(slots) * (1 + Math.min(magnitude - 1, 0.8) * 0.22);
};

/**
 * @param {object} props
 * @param {object[]} props.leads the same deck the desktop cycle uses.
 * @param {number} props.hotScore score at or above which a card stamps HOT.
 * @param {string} props.scoreLabel
 * @param {string} props.hotStamp
 * @param {boolean} [props.isHebrew]
 * @param {boolean} [props.enabled] the hero is on screen, the tab is visible
 *   and motion is welcome - the gate the auto-advance and the count-up share.
 * @param {boolean} [props.motionOff] the visitor asked us to stop animating.
 */
export default function HeroCarouselLive({
  leads,
  hotScore,
  scoreLabel,
  hotStamp,
  isHebrew = true,
  enabled = false,
  motionOff = false,
}) {
  const t = isHebrew ? CAROUSEL_COPY.he : CAROUSEL_COPY.en;
  const total = leads.length;
  // Which way the deck runs. RTL reads right to left, so card n+1 waits on the
  // LEFT and a card leaving goes right; LTR is the mirror. Transforms are
  // physical, so this is the one place the page's direction is spelled out.
  const dirSign = isHebrew ? -1 : 1;

  // Stable ref OBJECTS, one per slide: a fresh callback ref per render would be
  // a changed prop on a memoised card, exactly what `slideCardSx` avoids above.
  const slideRefs = useMemo(() => leads.map(() => React.createRef()), [leads]);
  const scoreRefs = useMemo(() => leads.map(() => React.createRef()), [leads]);
  const barRefs = useMemo(() => leads.map(() => React.createRef()), [leads]);

  // The motion state AT MOUNT decides what each card is RENDERED with, and it
  // must not change afterwards: React would then diff a `score` prop against
  // the DOM the count-up has been writing behind its back. A later flip of the
  // switch is handled imperatively, by the effect below.
  const staticAtMount = useRef(motionOff).current;
  const initialScores = useMemo(
    () => leads.map((lead) => (staticAtMount ? lead.score : 0)),
    [leads, staticAtMount]
  );

  const [current, setCurrent] = useState(0);
  const [hotFlags, setHotFlags] = useState(() =>
    leads.map((lead) => staticAtMount && lead.score >= hotScore)
  );
  // Which cards have already been counted. A Set rather than state: it decides
  // whether a count STARTS, and a re-render is not what should trigger one.
  const countedRef = useRef(null);
  if (countedRef.current === null) {
    countedRef.current = new Set(staticAtMount ? leads.map((_, index) => index) : []);
  }
  // The index the timer and the drag advance FROM. React batches the state
  // write, so between a turn being decided and the next commit the state is a
  // render behind - and a timer firing in that window would turn from a
  // position the wheel has already left.
  const currentRef = useRef(0);
  const motionOffRef = useRef(motionOff);
  motionOffRef.current = motionOff;

  // ------------------------------------------------------------- the score

  const writeScore = useCallback(
    (index, value) => {
      const figure = scoreRefs[index]?.current;
      if (figure) figure.textContent = `${value}%`;
      // The bar is set to the FINAL score as the count starts and its 1.4s ease
      // carries it there while the number counts up over 1.2s, so the bar leads
      // the number - the prototype's behaviour, and HeroLive.writeScore's.
      const bar = barRefs[index]?.current;
      if (bar) bar.style.width = `${value > 0 ? leads[index].score : 0}%`;
    },
    [barRefs, scoreRefs, leads]
  );

  const stampHot = useCallback((index) => {
    setHotFlags((prev) => {
      if (prev[index]) return prev;
      const next = prev.slice();
      next[index] = true;
      return next;
    });
  }, []);

  /**
   * A card counts its score the FIRST time it becomes the current slide, and
   * keeps that figure and its HOT stamp from then on.
   *
   * THE TICKER IS PER COUNT, and the cleanup stops it - which is what makes
   * this correct under React's StrictMode double-invocation in development.
   * A component-lifetime ticker torn down by the simulated unmount resolved the
   * first count early and left the card marked as counted, so on the dev server
   * every card snapped to its figure with no count at all, while production
   * (where effects are not double-invoked) counted properly. A per-count ticker
   * that the cleanup stops is simply restarted by the re-run.
   *
   * `ticker.isAlive()` inside `then` is what tells a real completion from a
   * teardown: `stop()` resolves the count too, with the target written, so an
   * abandoned card is left LOOKING finished but is not marked counted - the next
   * time it is centred it counts again and takes its stamp then.
   */
  useEffect(() => {
    const index = current;
    const lead = leads[index];
    if (!lead || countedRef.current.has(index)) return undefined;
    if (!enabled && !motionOff) return undefined;

    const settle = () => {
      countedRef.current.add(index);
      writeScore(index, lead.score);
      if (lead.score >= hotScore) stampHot(index);
    };

    if (motionOff) {
      settle();
      return undefined;
    }

    const ticker = createTicker();
    countUp(lead.score, SCORE_COUNT_MS, (value) => writeScore(index, value), ticker).then(() => {
      if (!ticker.isAlive()) return;
      settle();
    });
    return () => ticker.stop();
  }, [current, enabled, hotScore, leads, motionOff, stampHot, writeScore]);

  // The switch flipped mid-visit: every card jumps to its finished state. This
  // runs AFTER the count effect's cleanup above (React tears every effect down
  // before it re-runs any of them), so it is the last word.
  useEffect(() => {
    if (!motionOff) return;
    leads.forEach((lead, index) => {
      countedRef.current.add(index);
      writeScore(index, lead.score);
      if (lead.score >= hotScore) stampHot(index);
    });
  }, [hotScore, leads, motionOff, stampHot, writeScore]);

  // -------------------------------------------------------------- the wheel

  /**
   * Place every slide for a (possibly fractional) wheel position.
   *
   * The ONLY writer of a slide's presentation, and deliberately imperative: a
   * finger produces a new position per frame, and routing sixty of those a
   * second through setState would re-render eight memoised cards' worth of tree
   * for a value only the compositor needs.
   *
   * `animate` is false for the drag's own frames (the finger IS the timing) and
   * whenever motion is off (a turn is then a cut). Note the transition string
   * and the transform are written in the SAME pass: the browser computes a
   * transition from the after-change style, so re-arming the transition and
   * moving the card together is what makes the release animate.
   */
  const applyLayout = useCallback(
    (pos, animate) => {
      const turn = animate
        ? `transform ${WHEEL_TURN_MS}ms ${WHEEL_EASE}, opacity ${WHEEL_TURN_MS}ms ${WHEEL_EASE}`
        : "none";
      slideRefs.forEach((ref, index) => {
        const node = ref.current;
        if (!node) return;
        const d = wrapDelta(index, pos, total);
        const distance = Math.abs(d);
        // 0 at the centre, ±1 either side: the card's on-screen side. Both the
        // rotation and the translate take it, which is what bends the row into
        // a cylinder instead of fanning it out flat.
        const side = d === 0 ? 0 : Math.sign(d) * dirSign;
        const parked = distance > RENDERED_SPAN + 0.5;

        node.style.transform =
          `rotateY(${(side * at(ANCHORS.rotate, distance)).toFixed(2)}deg) ` +
          `translateX(${(side * at(ANCHORS.translate, distance)).toFixed(2)}%) ` +
          `translateZ(${at(ANCHORS.depth, distance).toFixed(1)}px) ` +
          `scale(${at(ANCHORS.scale, distance).toFixed(3)})`;
        node.style.opacity = `${at(ANCHORS.opacity, distance).toFixed(3)}`;
        node.style.zIndex = `${Math.max(1, 10 - Math.round(distance * 3))}`;

        // A card leaving the wheel must FADE and then go, or it blinks out at
        // opacity .18 while the rest of the turn is still running. A zero-length
        // `visibility` transition delayed by the turn does that; on the way back
        // the new declaration has no visibility in its list, so it is instant.
        node.style.transition = parked
          ? `${turn === "none" ? "" : `${turn}, `}visibility 0s linear ${
              animate ? WHEEL_TURN_MS : 0
            }ms`
          : turn;
        node.style.visibility = parked ? "hidden" : "visible";
        // Side cards are decoration for the current one, so a screen reader
        // gets exactly one card - the centred one - and `inert` keeps the rest
        // off every sequential-focus and hit-testing path as well.
        //
        // The current slide is also the KEYBOARD control (round 5b removed the
        // dot row): it is the wheel's single tab stop, it announces "3 of 8",
        // and the arrow keys handled below turn from it. Its `tabIndex` is
        // written here rather than rendered so that this function stays the one
        // writer of everything that distinguishes a slide from its neighbours.
        if (distance < 0.5) {
          node.removeAttribute("aria-hidden");
          node.removeAttribute("inert");
          node.tabIndex = 0;
        } else {
          node.setAttribute("aria-hidden", "true");
          node.setAttribute("inert", "");
          node.removeAttribute("tabindex");
        }
      });
    },
    [dirSign, slideRefs, total]
  );

  // Whether the wheel has ever been placed. See below - it is the difference
  // between a placement and a turn.
  const placedRef = useRef(false);

  // Before the first paint, and before any painted frame of a turn. A plain
  // effect would let one frame through with every card stacked at the centre.
  //
  // THE FIRST PASS IS NEVER ANIMATED. The slides have only just been inserted,
  // so their before-change style is the un-transformed, fully opaque one React
  // rendered; arming the transition on that pass would fan the whole deck out
  // of the centre on the first paint AND fade the three parked cards down from
  // fully visible, which is a 1.2s entrance nobody asked for on a page whose
  // hero is already busy.
  useLayoutEffect(() => {
    currentRef.current = current;
    applyLayout(current, placedRef.current && !motionOff);
    placedRef.current = true;
  }, [applyLayout, current, motionOff]);

  // ------------------------------------------------------ the auto-advance

  // Set by the effect below while the timer is live, and by nothing else. The
  // pointer handlers reach the timer through it rather than owning one, so
  // there is exactly one timeout in existence at any moment.
  const timerRef = useRef({ arm: () => {}, cancel: () => {} });

  useEffect(() => {
    if (!enabled || motionOff || total < 2) return undefined;

    let id = 0;
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      setCurrent(wrapIndex(currentRef.current + 1, total));
      id = setTimeout(tick, AUTO_ADVANCE_MS);
    };
    const controls = {
      arm: (delay) => {
        if (cancelled) return;
        clearTimeout(id);
        id = setTimeout(tick, delay);
      },
      cancel: () => clearTimeout(id),
    };
    timerRef.current = controls;
    controls.arm(AUTO_ADVANCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(id);
      timerRef.current = { arm: () => {}, cancel: () => {} };
    };
  }, [enabled, motionOff, total]);

  // ---------------------------------------------------------- the finger

  // Live gesture state. A ref, not state: none of it is rendered, and all of it
  // changes per pointer event.
  const dragRef = useRef(null);
  // The latest finger offset, read by the throttled frame rather than passed
  // to it - rafThrottle coalesces calls, so the frame must fetch the newest
  // value itself instead of animating a stale one.
  const dragOffsetRef = useRef(0);

  const dragFrame = useMemo(
    () =>
      rafThrottle(() => {
        const drag = dragRef.current;
        if (!drag || !drag.active) return;
        const slots = rubberBand(-dragOffsetRef.current / (dirSign * drag.unit));
        applyLayout(currentRef.current + slots, false);
      }),
    [applyLayout, dirSign]
  );
  useEffect(() => () => dragFrame.cancel(), [dragFrame]);

  const endGesture = useCallback(
    (drag) => {
      dragFrame.cancel();
      dragRef.current = null;
      if (!drag) return;
      if (!drag.active) {
        // The gesture was the page's (a vertical scroll, or a tap that never
        // moved). Nothing to snap - just give the timer its pause back.
        timerRef.current.arm(MANUAL_PAUSE_MS);
        return;
      }
      const slots = rubberBand(-drag.offset / (dirSign * drag.unit));
      // Velocity in SLOTS per ms, sign-corrected the same way, so a flick is
      // read in the wheel's own units rather than in screen pixels.
      const flick = -drag.velocity / (dirSign * drag.unit);
      let step = clamp(Math.round(slots), -1, 1);
      if (Math.abs(drag.velocity) > FLICK_PX_PER_MS) step = flick > 0 ? 1 : -1;

      if (step === 0) {
        // Snapping BACK is not a state change, so nothing would re-run the
        // layout effect: place it here, with the turn's own easing.
        applyLayout(currentRef.current, !motionOffRef.current);
      } else {
        setCurrent(wrapIndex(currentRef.current + step, total));
      }
      timerRef.current.arm(MANUAL_PAUSE_MS);
    },
    [applyLayout, dirSign, dragFrame, total]
  );

  const onPointerDown = useCallback(
    (event) => {
      if (event.pointerType === "mouse" && event.button !== 0) return;
      const node = slideRefs[currentRef.current]?.current;
      const width = node?.getBoundingClientRect?.().width || 0;
      timerRef.current.cancel();
      dragRef.current = {
        id: event.pointerId,
        // The card's own width times the fraction of it a neighbour's centre
        // actually travels: the card follows the finger 1:1.
        unit: Math.max(1, width * SLOT_TRAVEL),
        startX: event.clientX,
        startY: event.clientY,
        lastX: event.clientX,
        lastAt: typeof performance !== "undefined" ? performance.now() : Date.now(),
        offset: 0,
        velocity: 0,
        decided: false,
        active: false,
      };
      dragOffsetRef.current = 0;
    },
    [slideRefs]
  );

  const onPointerMove = useCallback(
    (event) => {
      const drag = dragRef.current;
      if (!drag || drag.id !== event.pointerId) return;
      const dx = event.clientX - drag.startX;
      const dy = event.clientY - drag.startY;
      if (!drag.decided) {
        if (Math.abs(dx) < GESTURE_SLOP && Math.abs(dy) < GESTURE_SLOP) return;
        drag.decided = true;
        // A gesture that is mostly vertical belongs to the page. `touch-action:
        // pan-y` already lets the browser scroll it; this keeps the wheel from
        // ALSO turning under a finger that was only scrolling past.
        drag.active = Math.abs(dx) > Math.abs(dy);
        if (!drag.active) return;
        // Capture only ONCE the gesture is ours, so a finger that turns out to
        // be scrolling the page vertically never has its pointer taken from the
        // scroller. From here a finger that wanders off the wheel still ends
        // its gesture in these handlers rather than leaving it half-dragged.
        if (typeof event.currentTarget.setPointerCapture === "function") {
          try {
            event.currentTarget.setPointerCapture(event.pointerId);
          } catch (err) {
            /* a pointer the browser has already released - nothing to capture */
          }
        }
      }
      if (!drag.active) return;
      const now = typeof performance !== "undefined" ? performance.now() : Date.now();
      if (now > drag.lastAt) {
        drag.velocity = (event.clientX - drag.lastX) / (now - drag.lastAt);
        drag.lastX = event.clientX;
        drag.lastAt = now;
      }
      drag.offset = dx;
      dragOffsetRef.current = dx;
      dragFrame.handler();
    },
    [dragFrame]
  );

  const onPointerUp = useCallback(
    (event) => {
      const drag = dragRef.current;
      if (!drag || drag.id !== event.pointerId) return;
      // A finger that stopped before it lifted is not still flicking.
      const now = typeof performance !== "undefined" ? performance.now() : Date.now();
      if (now - drag.lastAt > 90) drag.velocity = 0;
      endGesture(drag);
    },
    [endGesture]
  );

  const onPointerCancel = useCallback(
    (event) => {
      const drag = dragRef.current;
      if (!drag || drag.id !== event.pointerId) return;
      // The browser took the gesture (it became a page scroll). Put the wheel
      // back where it was rather than committing a turn the visitor never asked
      // for.
      dragFrame.cancel();
      dragRef.current = null;
      applyLayout(currentRef.current, !motionOffRef.current);
      timerRef.current.arm(MANUAL_PAUSE_MS);
    },
    [applyLayout, dragFrame]
  );

  // ------------------------------------------------------------ the keyboard

  // A keyboard turn has to carry focus with it: the slide the visitor was
  // focused on becomes `inert` the moment the wheel moves, which blurs it and
  // drops them out of the wheel after a single press. Set on the way in, spent
  // by the effect below.
  const refocusRef = useRef(false);

  const onKeyDown = useCallback(
    (event) => {
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      // The arrow that POINTS at the incoming card advances, in either
      // direction of the page: RTL keeps the next card on the left, LTR on the
      // right, and `dirSign` is already that fact.
      const step = event.key === "ArrowLeft" ? -dirSign : dirSign;
      // Or the page scrolls sideways under the wheel.
      event.preventDefault();
      refocusRef.current = true;
      setCurrent(wrapIndex(currentRef.current + step, total));
      // An arrow press is a manual choice and buys the same quiet a swipe does.
      timerRef.current.arm(MANUAL_PAUSE_MS);
    },
    [dirSign, total]
  );

  useEffect(() => {
    if (!refocusRef.current) return;
    refocusRef.current = false;
    // `preventScroll`: the hero is tall on a phone and pulling the page to the
    // wheel on every arrow press is not what the visitor asked for.
    slideRefs[current]?.current?.focus?.({ preventScroll: true });
  }, [current, slideRefs]);

  return (
    <Box className="hl-hero-carousel">
      <Box
        className="hl-hero-wheel"
        role="region"
        aria-roledescription="carousel"
        aria-label={t.label}
        // The keyboard control is the CURRENT SLIDE, not this box: it is the
        // wheel's single tab stop (applyLayout gives it `tabIndex = 0` and takes
        // it off the others), it announces its own position in the deck, and
        // the arrows bubble up to here from it. One tab stop, and it is the
        // thing the arrows act on.
        onKeyDown={onKeyDown}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
      >
        {leads.map((lead, index) => (
          <Box
            key={`${index}-${lead.pro}`}
            ref={slideRefs[index]}
            className="hl-hero-slide"
            role="group"
            aria-roledescription="slide"
            aria-label={t.slide(index + 1, total)}
          >
            <LeadCardLive
              lead={lead}
              score={initialScores[index]}
              scoreRef={scoreRefs[index]}
              barRef={barRefs[index]}
              hot={hotFlags[index]}
              scoreLabel={scoreLabel}
              hotStamp={hotStamp}
              sx={slideCardSx}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
}
